import clsx from 'clsx';
import { useMemo, Activity, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { IconSearch } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import ListSkeleton from '@/components/SkeletonLoader/ListSkeleton';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import {
  getCommonStatusLabel,
  getCommonStatusBadgeClass,
} from '@/constants/constant';
import { ECommonStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getClassDropdown } from '@/service/classService';
import {
  getDeviceList,
  getDeviceSettings,
  updateDeviceSettings,
} from '@/service/controlService';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { IClassDropdownItem } from '@/types';
import { IDevice } from '@/types/device';
import { IDeviceSettingsPayload } from '@/types/payload';
import { getAssetUrl, formatLastActive } from '@/utils';

import DeviceDetailsModal from './components/DeviceDetailsModal';

interface IControlsForm {
  search: string;
  active_hours_per_session: string;
  break_interval: string;
  start_hour: number;
  end_hour: number;
}

const SLIDER_MIN_HOUR = 2;
const SLIDER_MAX_HOUR = 26;

const hourToLabel = (hour: number) => {
  const normalizedHour = ((hour % 24) + 24) % 24;
  const safeHour = Math.min(Math.max(normalizedHour, 0), 23);
  const period = safeHour >= 12 ? 'PM' : 'AM';
  const displayHour = safeHour % 12 === 0 ? 12 : safeHour % 12;
  return `${displayHour}:00 ${period}`;
};

const createDeviceColumns = (
  formatText: (key: string) => string,
  handleSort: (key: string) => void
) => [
  {
    key: 'device_name',
    title: 'controls.device-management.columns.device-name',
    dataIndex: 'device_name',
    width: '330px',
    sorter: true,
    handleSort: () => handleSort('device_name'),
    render: (record: IDevice) => (
      <span className="text-sm text-neutral-primary">
        {record.device_name ?? '-'}
      </span>
    ),
  },
  {
    key: 'device_id',
    title: 'controls.device-management.columns.device-id',
    dataIndex: 'device_id',
    width: '282px',
    sorter: true,
    handleSort: () => handleSort('device_id'),
    render: (record: IDevice) => (
      <span className="text-sm text-neutral-primary">{record.device_id}</span>
    ),
  },
  {
    key: 'class_name',
    title: 'controls.device-management.columns.assigned-class',
    dataIndex: 'class_name',
    width: '283px',
    sorter: true,
    handleSort: () => handleSort('class_name'),
    render: (record: IDevice) => (
      <span className="text-sm text-neutral-primary">
        {record.class_name ?? '-'}
      </span>
    ),
  },
  {
    key: 'student_name',
    title: 'controls.device-management.columns.student-name',
    dataIndex: 'student_name',
    width: '305px',
    sorter: true,
    handleSort: () => handleSort('student_name'),
    render: (record: IDevice) => (
      <div className="flex items-center gap-2.5">
        <UserAvatarList
          user={record.student_name}
          image={getAssetUrl(record?.profile_pic)}
          className="w-8 h-8 min-w-8"
        />
        <span className="text-sm text-neutral-primary">
          {record.student_name}
        </span>
      </div>
    ),
  },
  {
    key: 'status',
    title: 'controls.device-management.columns.status',
    dataIndex: 'status',
    width: '189px',
    sorter: true,
    handleSort: () => handleSort('status'),
    render: (record: IDevice) => {
      const statusKey = record.status as ECommonStatus;

      return statusKey ? (
        <TagCapsule
          value={formatText(getCommonStatusLabel(statusKey))}
          className={getCommonStatusBadgeClass(statusKey)}
        />
      ) : (
        <span className="text-sm text-neutral-primary">-</span>
      );
    },
  },

  {
    key: 'last_active',
    title: 'controls.device-management.columns.last-active',
    dataIndex: 'last_active',
    width: '168px',
    sorter: true,
    handleSort: () => handleSort('last_active'),
    render: (record: IDevice) => (
      <span className="text-sm text-neutral-primary">
        {record.last_active
          ? formatLastActive(record.last_active, formatText)
          : '-'}
      </span>
    ),
  },
];

const Controls = () => {
  const { formatText } = useFormatText();
  const { setTitle } = usePageTitle();
  const language = useSettingsStore((state) => state.settings.language);
  const pageSize = 10;

  const {
    register,
    watch,
    reset,
    getValues,
    setValue,
    formState: { isDirty },
  } = useForm<IControlsForm>({
    defaultValues: {
      search: '',
      active_hours_per_session: '1',
      break_interval: '10',
      start_hour: 8,
      end_hour: 14,
    },
  });

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ASC' | 'DESC';
  } | null>(null);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'ASC' ? 'DESC' : 'ASC' };
      }
      return { key, direction: 'ASC' };
    });
  };

  const [selectedClass, setSelectedClass] = useState('');
  const [classOptions, setClassOptions] = useState<IClassDropdownItem[]>([]);
  const startHour = watch('start_hour');
  const endHour = watch('end_hour');
  const isRtl = language === 'ar';
  const debouncedSearch = useDebounce(watch('search') ?? '', 300);

  // Device list state
  const [devices, setDevices] = useState<IDevice[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Device details modal state
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);

  const { isScreenLoading, isTableLoading, setScreenLoading, setTableLoading } =
    useSkeletonStore();

  const columns = useMemo(
    () => createDeviceColumns(formatText, handleSort),
    [formatText]
  );

  useEffect(() => {
    setTitle(formatText('controls.title'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    const fetchClassDropdown = async () => {
      const res = await getClassDropdown();
      if (res?.data) {
        setClassOptions(res.data ?? []);
      }
    };
    fetchClassDropdown();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      const res = await getDeviceSettings();
      if (res?.data) {
        const {
          start_time,
          end_time,
          active_hours_per_session,
          break_interval,
        } = res.data;

        reset({
          search: getValues('search'),
          active_hours_per_session: active_hours_per_session?.toString() || '1',
          break_interval: break_interval?.toString() || '10',
          start_hour: start_time ? getHourFromTimestamp(start_time) : 8,
          end_hour: end_time ? getHourFromTimestamp(end_time) : 14,
        });
      }
    };
    fetchSettings();
  }, [reset, getValues]);

  // Reset page on filter/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedClass, sortConfig]);

  // Fetch devices
  const fetchDevices = async () => {
    if (devices.length === 0 && !debouncedSearch && !selectedClass) {
      setScreenLoading(true);
    } else {
      setTableLoading(true);
    }

    try {
      const query: Record<string, unknown> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };

      if (debouncedSearch) query.search = debouncedSearch;
      if (selectedClass) query['class_id[0]'] = selectedClass;

      if (sortConfig?.key && sortConfig?.direction) {
        query[`order[${sortConfig.key}]`] = sortConfig.direction;
      } else {
        query['order[status]'] = 'DESC';
      }

      const res = await getDeviceList(query);
      if (res?.data) {
        setDevices(res.data ?? []);
        setTotal(res.total ?? res.data.length ?? 0);
      }
    } finally {
      setScreenLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, selectedClass, sortConfig]);

  const getHourFromTimestamp = (timestamp: string) => {
    const date = new Date(Number(timestamp));
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0);

    const diffMs = date.getTime() - baseDate.getTime();
    return Math.round(diffMs / (1000 * 60 * 60));
  };

  const getTimestampForHour = (hour: number) => {
    const date = new Date();
    date.setHours(hour, 0, 0, 0);
    return date.getTime().toString();
  };

  const handleSave = async () => {
    const values = getValues();
    const payload: IDeviceSettingsPayload = {
      start_time: getTimestampForHour(values.start_hour),
      end_time: getTimestampForHour(values.end_hour),
      hourly_session: Number(values.active_hours_per_session),
      break_interval: Number(values.break_interval),
    };

    const res = await updateDeviceSettings(payload);
    if (res) {
      reset(values);
    }
  };

  const handleRowClick = (id: string) => {
    setSelectedDeviceId(id);
    setIsDeviceModalOpen(true);
  };

  const handleCloseDeviceModal = () => {
    setIsDeviceModalOpen(false);
    setSelectedDeviceId(null);
  };

  const handleStartHourChange = (value: number) => {
    const nextValue = Math.min(value, endHour - 1);
    setValue('start_hour', nextValue, { shouldDirty: true });
  };

  const handleEndHourChange = (value: number) => {
    const nextValue = Math.max(value, startHour + 1);
    setValue('end_hour', nextValue, { shouldDirty: true });
  };

  const sliderRange = SLIDER_MAX_HOUR - SLIDER_MIN_HOUR;
  const startPosition = ((startHour - SLIDER_MIN_HOUR) / sliderRange) * 100;
  const endPosition = ((endHour - SLIDER_MIN_HOUR) / sliderRange) * 100;

  const isEmptySearch =
    !isScreenLoading &&
    devices.length === 0 &&
    (!!debouncedSearch || !!selectedClass);

  if (isScreenLoading) {
    return <ListSkeleton columns={columns} />;
  }

  return (
    <div className="flex flex-col gap-6" data-testid="controls-page-container">
      <div className="flex items-center gap-2.5">
        <div data-testid="controls-search-input" className="w-full max-w-87.5">
          <Input
            name="search"
            register={register}
            placeholder={formatText('controls.filters.search-placeholder')}
            leftIcon={{
              icon: <IconSearch size={18} className="text-neutral-500" />,
            }}
            className="w-full"
            inputClassName="!py-3.5"
          />
        </div>

        <div data-testid="controls-class-select">
          <Select
            options={classOptions}
            selected={selectedClass}
            onChange={(option: string | string[]) => {
              const value = Array.isArray(option) ? option[0] || '' : option;
              setSelectedClass(value);
            }}
            isClearable
            placeholder={formatText('controls.filters.select-class')}
            className="py-3.5!"
            rootClassName="w-[150px]"
          />
        </div>
      </div>

      <section
        className="flex flex-col gap-6"
        data-testid="screen-time-section"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[20px] leading-7 font-semibold tracking-[-0.2px] text-neutral-primary">
            {formatText('controls.screen-time.title')}
          </h2>

          <div data-testid="controls-save-button">
            <Button
              label={formatText('common.actions.save-changes')}
              variant="primary"
              className="h-10 px-6"
              onClick={handleSave}
              disabled={!isDirty}
            />
          </div>
        </div>

        <div className="border border-neutral-light-200 rounded-2xl p-4 flex gap-12 items-start max-lg:flex-col">
          <div className="w-full max-w-130.5 flex flex-col gap-15  relative">
            <p className="text-base leading-6 text-neutral-primary">
              {formatText('controls.screen-time.learning-hours')}
            </p>

            <div className="relative h-10" data-testid="controls-time-slider">
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 bg-neutral-light-50 rounded-full" />

              <div
                className="absolute top-1/2 -translate-y-1/2 h-2 bg-brand-default rounded-full"
                style={{
                  insetInlineStart: `${startPosition}%`,
                  width: `${Math.max(endPosition - startPosition, 0)}%`,
                }}
              />

              <input
                data-testid="controls-start-hour"
                type="range"
                min={SLIDER_MIN_HOUR}
                max={SLIDER_MAX_HOUR}
                value={startHour}
                onChange={(event) =>
                  handleStartHourChange(Number(event.target.value))
                }
                className="absolute -top-1.5 inset-inline-start-0 z-3 h-full w-full cursor-pointer appearance-none bg-transparent pointer-events-none controls-range"
              />
              <input
                data-testid="controls-end-hour"
                type="range"
                min={SLIDER_MIN_HOUR}
                max={SLIDER_MAX_HOUR}
                value={endHour}
                onChange={(event) =>
                  handleEndHourChange(Number(event.target.value))
                }
                className="absolute -top-1.5 inset-inline-start-0 z-4 h-full w-full cursor-pointer appearance-none bg-transparent pointer-events-none controls-range"
              />

              <span
                data-testid="controls-start-hour-label"
                className={clsx(
                  'absolute -top-7 rounded-full text-center bg-brand-light-50 px-2 py-0.5 text-xs font-semibold text-brand-default',
                  isRtl ? 'translate-x-1/2' : '-translate-x-1/2'
                )}
                style={{ insetInlineStart: `${startPosition}%` }}
              >
                {hourToLabel(startHour)}
              </span>
              <span
                data-testid="controls-end-hour-label"
                className={clsx(
                  'absolute -top-7 rounded-full text-center bg-brand-light-50 px-2 py-0.5 text-xs font-semibold text-brand-default',
                  isRtl ? 'translate-x-1/2' : '-translate-x-1/2'
                )}
                style={{ insetInlineStart: `${endPosition}%` }}
              >
                {hourToLabel(endHour)}
              </span>
            </div>
          </div>

          <div className="flex w-full gap-12 max-md:flex-col max-md:gap-4">
            <div
              className="flex-1 flex flex-col gap-1.5"
              data-testid="controls-active-hours"
            >
              <Input
                name="active_hours_per_session"
                register={register}
                label={formatText('controls.screen-time.active-hours')}
              />
              <p className="text-xs leading-5 text-neutral-secondary">
                {formatText('controls.screen-time.active-hours-hint')}
              </p>
            </div>

            <div
              className="flex-1 flex flex-col gap-1.5"
              data-testid="controls-break-interval"
            >
              <Input
                name="break_interval"
                register={register}
                label={`${formatText('controls.screen-time.mandatory-break')} (${formatText('controls.screen-time.mandatory-break-unit')})`}
              />
              <p className="text-xs leading-5 text-neutral-secondary">
                {formatText('controls.screen-time.mandatory-break-hint')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section
        className="flex flex-col gap-6"
        data-testid="device-management-section"
      >
        <h2 className="text-[20px] leading-7 font-semibold tracking-[-0.2px] text-neutral-primary">
          {formatText('controls.device-management.title')}
        </h2>

        <div data-testid="controls-device-table">
          <CustomTable
            dataSource={devices}
            columns={columns}
            rowKey="id"
            total={total}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            isEmptySearch={isEmptySearch}
            className="gap-0!"
            isLoading={isTableLoading}
            fieldName={sortConfig?.key}
            sortDirection={sortConfig?.direction}
            onRowClick={handleRowClick}
            dataTestId="table"
          />
        </div>
      </section>

      {/* Device Details Modal */}
      <Activity mode={isDeviceModalOpen ? 'visible' : 'hidden'}>
        <DeviceDetailsModal
          isOpen={isDeviceModalOpen}
          onClose={handleCloseDeviceModal}
          deviceId={selectedDeviceId}
          onSuccess={fetchDevices}
        />
      </Activity>
    </div>
  );
};

export default Controls;
